import { ToastMessage, ToastType } from '../../types/types';
import React, { useEffect, useState } from 'react';

const TOAST_COLORS = {
  // Background colors by toast type — use design system tokens where available
  background: {
    success: 'var(--color-success)',
    error: 'var(--color-error)',
    info: 'rgba(29, 85, 182, 0.95)', // no info token yet — keep as-is
    warning: 'var(--color-warning)',
  },
  // Text color (same for all types)
  text: 'var(--color-content-inverse)',
  shadow: 'rgba(0, 0, 0, 0.3)',
};

// Toast duration in milliseconds
const TOAST_DURATION_MS = 3000;

interface ToastProps {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // Determine if this toast is dismissible (default: true)
  const isDismissible = toast.dismissible !== false;

  useEffect(() => {
    // Trigger enter animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // Use custom duration if provided, otherwise use default
    const duration = toast.duration || TOAST_DURATION_MS;

    // Auto-dismiss after duration
    const timer = setTimeout(() => {
      setIsLeaving(true);
      setTimeout(() => {
        onDismiss(toast.id);
      }, 300); // Match exit animation duration
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  const handleDismiss = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDismissible) return;

    e.stopPropagation();
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 300);
  };

  return (
    <div
      onClick={handleDismiss}
      onTouchEnd={handleDismiss}
      style={{
        padding: '12px 20px',
        borderRadius: '12px',
        backgroundColor: TOAST_COLORS.background[toast.type],
        color: TOAST_COLORS.text,
        boxShadow: `0 4px 12px ${TOAST_COLORS.shadow}`,
        fontSize: '14px',
        textTransform: 'lowercase',
        fontWeight: 500,
        maxWidth: '90vw',
        textAlign: 'center',
        transform: isVisible && !isLeaving ? 'translateY(0)' : 'translateY(-20px)',
        opacity: isVisible && !isLeaving ? 1 : 0,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        lineHeight: 1.4,
        cursor: isDismissible ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      {toast.message}
    </div>
  );
};

// Toast Container - manages multiple toasts
interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--header-height) + 16px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10001, // Above refresh button (which is likely around 9999-10000)
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        pointerEvents: 'auto', // Enable pointer events for dismissal
      }}
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

// Hook to manage toasts
export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (
    message: string,
    type: ToastType = 'info',
    duration?: number,
    dismissible = true
  ) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    setToasts(prev => [...prev, { id, message, type, duration, dismissible }]);
  };

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return {
    toasts,
    showToast,
    dismissToast,
  };
}

export default Toast;
